import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { updateIntegrationSecrets, deleteIntegrationSecret } from "@/lib/api";

/**
 * Edit / rotate keys for a single integration.
 * Empty fields are ignored on save (leave existing values intact).
 * "Clear stored value" deletes a DB-stored secret, falling back to env.
 */
export default function EditKeysDialog({ open, service, reauthToken, onClose, onSaved }) {
  const [values, setValues] = useState({});
  const [reveal, setReveal] = useState({});
  const [busy, setBusy] = useState(false);
  const [clearing, setClearing] = useState(null);

  if (!service) return null;

  const handleSave = async () => {
    const updates = {};
    for (const k of service.keys) {
      const v = (values[k.name] || "").trim();
      if (v) updates[k.name] = v;
    }
    if (Object.keys(updates).length === 0) {
      toast.info("Nothing to save — fill at least one field, or use 'Clear' next to a key.");
      return;
    }
    setBusy(true);
    try {
      const res = await updateIntegrationSecrets(service.key, updates, reauthToken);
      toast.success(`Saved ${res.updated.length} key(s) for ${service.name}.`);
      setValues({});
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async (keyName) => {
    if (!window.confirm(`Clear the stored value for ${keyName}? It will fall back to environment value if any.`)) return;
    setClearing(keyName);
    try {
      await deleteIntegrationSecret(service.key, keyName, reauthToken);
      toast.success(`${keyName} cleared.`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to clear");
    } finally {
      setClearing(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="sm:max-w-lg" data-testid={`edit-keys-${service.key}`}>
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center h-9 w-9 rounded-full"
                  style={{ background: "rgba(11,37,69,0.08)", color: "#0B2545" }}>
              <KeyRound className="h-4.5 w-4.5" />
            </span>
            <div>
              <DialogTitle>Edit · {service.name}</DialogTitle>
              <DialogDescription>
                Paste new values to update or rotate. Leave blank to keep current. Values are encrypted before storage.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-2 max-h-[60vh] overflow-y-auto pr-1">
          {service.keys.map((k) => {
            const isRevealed = reveal[k.name];
            return (
              <div key={k.name} className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor={`key-${k.name}`} className="text-xs uppercase tracking-wider font-medium" style={{ color: "#0B2545" }}>
                    {k.label}
                    {k.required && <span style={{ color: "#9B1C1C" }}> *</span>}
                  </Label>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
                    {k.is_set ? (
                      <span className="px-1.5 py-0.5 rounded" style={{ background: "rgba(22,101,52,0.10)", color: "#166534" }}>
                        {k.source === "database" ? "Stored" : "From env"}
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded" style={{ background: "rgba(155,28,28,0.08)", color: "#9B1C1C" }}>
                        Missing
                      </span>
                    )}
                  </div>
                </div>

                {k.is_set && (
                  <div className="text-xs font-mono px-2 py-1 rounded border"
                       style={{ borderColor: "#E2E8F0", background: "#F8FAFC", color: "#475569" }}
                       data-testid={`current-${k.name}`}>
                    {k.masked || (k.secret ? "(set)" : "(empty)")}
                  </div>
                )}

                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      id={`key-${k.name}`}
                      type={k.secret && !isRevealed ? "password" : "text"}
                      value={values[k.name] || ""}
                      onChange={(e) => setValues((v) => ({ ...v, [k.name]: e.target.value }))}
                      placeholder={`Paste new ${k.label}…`}
                      className="font-mono text-sm pr-9"
                      data-testid={`input-${k.name}`}
                      autoComplete="off"
                    />
                    {k.secret && (
                      <button
                        type="button"
                        onClick={() => setReveal((r) => ({ ...r, [k.name]: !r[k.name] }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded hover:bg-slate-100"
                        style={{ color: "#64748B" }}
                        title={isRevealed ? "Hide" : "Reveal what you've typed"}
                      >
                        {isRevealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                  {k.is_set && k.source === "database" && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleClear(k.name)}
                      disabled={clearing === k.name}
                      data-testid={`clear-${k.name}`}
                      title="Clear stored value"
                    >
                      {clearing === k.name ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={busy}
            style={{ background: "#0B2545", color: "#fff" }}
            data-testid="save-secrets"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
